import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { PLAN_META } from "@/lib/planFeatures";
import api from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { reload } = useAuth();
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const [state, setState] = useState("checking");
  const [plan, setPlan] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let stopped = false;
    let timer;
    setState("checking");
    const poll = async (count = 0) => {
      if (!sessionId) { setState("invalid"); return; }
      try {
        const { data } = await api.get(`/payments/status/${encodeURIComponent(sessionId)}`);
        if (stopped) return;
        if (data.payment_status === "paid") {
          setState(data.requires_review ? "review" : data.active ? "paid" : "received"); setPlan(data.plan);
          await reloadRef.current(); return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState(data.payment_status); return; }
      } catch (e) {
        if (stopped) return;
        if ([401, 403].includes(e?.response?.status)) { setState("signin"); return; }
        if (e?.response?.status === 404) { setState("invalid"); return; }
        if (count >= 8) { setState("error"); return; }
      }
      if (count < 8) timer = setTimeout(() => poll(count + 1), 2000);
      else setState("pending");
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [sessionId, attempt]);

  const copy = {
    checking: ["Confirming your payment…", "This only takes a moment."],
    paid: [`You're on ${PLAN_META[plan]?.name || "your new plan"}!`, "Your subscription is active. Your paid features are ready."],
    received: ["Payment received", "This payment is recorded. Check Billing for your current subscription status."],
    review: ["Payment needs review", "This checkout used a discount that could not be authorized. Paid access has not been activated. Contact TenX before starting another payment."],
    pending: ["Payment processing", "Your payment hasn't been confirmed yet. You won't receive paid access until it clears."],
    expired: ["Checkout expired", "This checkout was not completed. Choose a plan to start again."],
    failed: ["Payment unsuccessful", "This payment did not complete. You can try again from Billing."],
    invalid: ["Checkout not found", "This checkout link is missing, invalid, or belongs to another account."],
    signin: ["Sign in to confirm payment", "Sign in with the founder account that started checkout, then check Billing."],
    error: ["Unable to confirm payment", "We couldn't reach billing right now. Retry before starting another payment."],
  };
  const [title, description] = copy[state];
  const paid = state === "paid" || state === "received";
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-5">
      <Link to="/" className="mb-8" data-testid="payment-home-link"><Logo /></Link>
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-10" data-testid="payment-success">
        {paid ? <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" /> : state === "checking" ? <Loader2 className="mx-auto h-12 w-12 animate-spin text-brand" /> : <XCircle className="mx-auto h-12 w-12 text-amber-600" />}
        <h1 className="ff-cabinet mt-6 text-2xl font-bold text-slate-900" data-testid="payment-result-title">{title}</h1>
        <p className="ff-manrope mt-3 text-sm text-slate-600" data-testid="payment-result-message">{description}</p>
        {state !== "checking" && <Button asChild className="ff-manrope mt-6 w-full gap-1.5" data-testid="success-continue"><Link to="/app/billing">Go to billing <ArrowRight className="h-4 w-4" /></Link></Button>}
        {["pending", "error"].includes(state) && <Button variant="outline" className="mt-2 w-full" onClick={() => setAttempt((n) => n + 1)} data-testid="payment-confirm-retry">Check again</Button>}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-5">
      <Link to="/" className="mb-8" data-testid="payment-cancel-home-link"><Logo /></Link>
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-10" data-testid="payment-cancel">
        <XCircle className="mx-auto h-14 w-14 text-slate-300" />
        <h1 className="ff-cabinet mt-6 text-2xl font-bold text-slate-900" data-testid="payment-cancel-title">Checkout canceled</h1>
        <p className="ff-manrope mt-2 text-sm text-slate-600" data-testid="payment-cancel-message">You left checkout. Your existing plan is unchanged; check Billing for any completed payments.</p>
        <Button asChild className="ff-manrope mt-6 w-full gap-1.5" data-testid="cancel-back"><Link to="/pricing">Back to pricing <ArrowRight className="h-4 w-4" /></Link></Button>
        <Button asChild variant="outline" className="ff-manrope mt-2 w-full" data-testid="cancel-to-dashboard"><Link to="/app">Continue to your dashboard</Link></Button>
      </div>
    </div>
  );
}